/**
 * 예약 상세 페이지
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Modal, Form, Alert } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { bookingService } from '../../services/bookingService';
import { Booking, BookingStatus } from '../../types/booking';
import { format } from 'date-fns';

const BookingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState<BookingStatus>('confirmed');
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { data: booking, isLoading, error } = useQuery({
    queryKey: ['booking', id],
    queryFn: () => bookingService.getBooking(Number(id)),
    enabled: !!id,
  });

  const statusMutation = useMutation({
    mutationFn: (status: BookingStatus) => bookingService.updateBookingStatus(Number(id), status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', id] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setShowStatusModal(false);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => bookingService.cancelBooking(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', id] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      setShowCancelModal(false);
    },
  });

  const getStatusBadge = (status: BookingStatus) => {
    const statusMap: Record<BookingStatus, { variant: string; label: string }> = {
      pending: { variant: 'warning', label: '대기' },
      confirmed: { variant: 'success', label: '확인됨' },
      cancelled: { variant: 'danger', label: '취소됨' },
      completed: { variant: 'info', label: '완료됨' },
      no_show: { variant: 'secondary', label: '노쇼' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return `₩${amount.toLocaleString()}`;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <p style={{ color: '#fff' }}>로딩 중...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error || !booking) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <Alert variant="danger">예약 정보를 불러올 수 없습니다.</Alert>
          <Button variant="secondary" onClick={() => navigate('/admin/bookings')}>
            목록으로
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>예약 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/bookings')} className="me-2">
              목록으로
            </Button>
            {booking.status !== 'cancelled' && (
              <>
                <Button
                  variant="primary"
                  onClick={() => {
                    setNewStatus('confirmed');
                    setShowStatusModal(true);
                  }}
                  className="me-2"
                >
                  상태 변경
                </Button>
                <Button
                  variant="danger"
                  onClick={() => setShowCancelModal(true)}
                >
                  예약 취소
                </Button>
              </>
            )}
          </div>
        </div>

        <Row>
          <Col md={8}>
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">예약 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">예약번호</label>
                      <div className="detail-value">{booking.booking_number}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">상태</label>
                      <div className="detail-value">{getStatusBadge(booking.status)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">방문일시</label>
                      <div className="detail-value">
                        {format(new Date(booking.visit_date), 'yyyy년 MM월 dd일')} {booking.visit_time}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">예약일시</label>
                      <div className="detail-value">
                        {format(new Date(booking.created_at), 'yyyy년 MM월 dd일 HH:mm')}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">고객 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">이름</label>
                      <div className="detail-value">
                        {booking.guest?.name || booking.member?.name || '-'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">이메일</label>
                      <div className="detail-value">
                        {booking.guest?.email || booking.member?.email || '-'}
                      </div>
                    </div>
                  </Col>
                  {booking.guest?.phone && (
                    <Col md={6}>
                      <div className="detail-item mb-3">
                        <label className="detail-label">전화번호</label>
                        <div className="detail-value">{booking.guest.phone}</div>
                      </div>
                    </Col>
                  )}
                </Row>
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>
                <h5 className="mb-0">상품 정보</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">상품명</label>
                      <div className="detail-value">
                        {booking.hall?.name || booking.package?.name || '-'}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item mb-3">
                      <label className="detail-label">인원</label>
                      <div className="detail-value">
                        성인 {booking.adult_count}명, 청소년 {booking.youth_count}명,
                        장애인 {booking.disabled_count}명, 유아 {booking.infant_count}명
                        <br />
                        <strong>총 {booking.adult_count + booking.youth_count + booking.disabled_count + booking.infant_count}명</strong>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">결제 정보</h5>
              </Card.Header>
              <Card.Body>
                <div className="detail-item mb-3">
                  <label className="detail-label">총 금액</label>
                  <div className="detail-value">{formatCurrency(booking.total_amount)}</div>
                </div>
                <div className="detail-item mb-3">
                  <label className="detail-label">할인 금액</label>
                  <div className="detail-value text-danger">
                    -{formatCurrency(booking.discount_amount)}
                  </div>
                </div>
                <div className="detail-item mb-3">
                  <label className="detail-label">최종 결제 금액</label>
                  <div className="detail-value" style={{ fontSize: '20px', fontWeight: 600, color: 'var(--color-accent-cyan)' }}>
                    {formatCurrency(booking.final_amount)}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 상태 변경 모달 */}
        <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>상태 변경</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>새 상태</Form.Label>
              <Form.Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as BookingStatus)}
              >
                <option value="pending">대기</option>
                <option value="confirmed">확인됨</option>
                <option value="completed">완료됨</option>
                <option value="no_show">노쇼</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
              닫기
            </Button>
            <Button
              variant="primary"
              onClick={() => statusMutation.mutate(newStatus)}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? '변경 중...' : '변경하기'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* 취소 확인 모달 */}
        <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>예약 취소 확인</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            정말로 이 예약을 취소하시겠습니까?
            <div className="mt-3">
              <p><strong>예약번호:</strong> {booking.booking_number}</p>
              <p><strong>고객명:</strong> {booking.guest?.name || booking.member?.name}</p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
              닫기
            </Button>
            <Button
              variant="danger"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? '취소 중...' : '취소하기'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

      <style>{`
        .detail-item {
          padding: 8px 0;
        }
        .detail-label {
          display: block;
          font-size: 12px;
          color: var(--color-text-secondary);
          margin-bottom: 4px;
          font-weight: 500;
        }
        .detail-value {
          font-size: 14px;
          color: var(--color-text-primary);
        }
      `}</style>
    </AdminLayout>
  );
};

export default BookingDetail;

